import ApplicationController from './application_controller';

/**
 * Expanding Textarea Controller
 *
 * A textarea that starts compact (like an input) and expands on focus or as content grows.
 * Collapses back when blurred if content is short.
 *
 * Usage:
 *   <textarea
 *     data-controller="expanding-textarea"
 *     data-expanding-textarea-min-rows-value="1"
 *     data-expanding-textarea-max-rows-value="6"
 *     data-expanding-textarea-expanded-rows-value="4"
 *     class="form-input resize-none"
 *     rows="1"
 *   ></textarea>
 *
 * Values:
 *   minRows: Minimum rows when collapsed (default: 1)
 *   maxRows: Maximum rows to expand to (default: 8)
 *   expandedRows: Rows to show on focus, before auto-sizing kicks in (default: 3)
 *
 * The textarea will:
 *   1. Start at minRows height
 *   2. Expand to expandedRows on focus
 *   3. Auto-grow beyond expandedRows if content requires it (up to maxRows)
 *   4. Shrink back to minRows on blur (if content fits)
 */
export default class extends ApplicationController {
  static values = {
    minRows: { type: Number, default: 1 },
    maxRows: { type: Number, default: 8 },
    expandedRows: { type: Number, default: 3 },
  };

  declare minRowsValue: number;
  declare maxRowsValue: number;
  declare expandedRowsValue: number;

  private lineHeight: number = 0;
  private paddingY: number = 0;
  private isExpanded: boolean = false;

  connect() {
    this.calculateMetrics();
    this.setMinHeight();
    this.setupTransition();
  }

  /**
   * Calculate line height and padding from computed styles
   */
  private calculateMetrics() {
    const computed = window.getComputedStyle(this.element as HTMLElement);
    this.lineHeight = parseFloat(computed.lineHeight) || 24;
    this.paddingY =
      parseFloat(computed.paddingTop) + parseFloat(computed.paddingBottom);
  }

  /**
   * Set up CSS transition for smooth animation
   */
  private setupTransition() {
    (this.element as HTMLElement).style.transition = 'height 0.2s ease-out';
    (this.element as HTMLElement).style.overflow = 'hidden';
  }

  /**
   * Set the minimum (collapsed) height
   */
  private setMinHeight() {
    const height = this.minRowsValue * this.lineHeight + this.paddingY;
    (this.element as HTMLElement).style.height = `${height}px`;
  }

  /**
   * Expand on focus
   */
  focus() {
    this.isExpanded = true;
    this.resize();
  }

  /**
   * Potentially collapse on blur
   */
  blur() {
    this.isExpanded = false;
    // Small delay to allow click events on nearby elements
    setTimeout(() => {
      if (!this.isExpanded) {
        this.resize();
      }
    }, 150);
  }

  /**
   * Auto-resize as user types
   */
  input() {
    this.resize();
  }

  /**
   * Calculate and set the appropriate height
   */
  private resize() {
    const textarea = this.element as HTMLTextAreaElement;
    const content = textarea.value;

    // If collapsed and content fits in min rows, stay collapsed
    if (!this.isExpanded) {
      const contentRows = this.countContentRows(content);
      if (contentRows <= this.minRowsValue) {
        this.setMinHeight();
        return;
      }
    }

    // Calculate target rows
    let targetRows: number;

    if (this.isExpanded) {
      // When focused, start at expandedRows minimum
      const contentRows = this.countContentRows(content);
      targetRows = Math.max(this.expandedRowsValue, contentRows);
    } else {
      // When blurred, fit content (but respect min)
      targetRows = Math.max(this.minRowsValue, this.countContentRows(content));
    }

    // Clamp to max
    targetRows = Math.min(targetRows, this.maxRowsValue);

    // Set height
    const height = targetRows * this.lineHeight + this.paddingY;
    textarea.style.height = `${height}px`;

    // If at max and content overflows, allow scroll
    if (targetRows >= this.maxRowsValue) {
      textarea.style.overflow = 'auto';
    } else {
      textarea.style.overflow = 'hidden';
    }
  }

  /**
   * Count how many rows the current content needs
   */
  private countContentRows(content: string): number {
    if (!content) return 1;

    const textarea = this.element as HTMLTextAreaElement;

    // Create a hidden div to measure
    const mirror = document.createElement('div');
    mirror.style.cssText = `
      position: absolute;
      visibility: hidden;
      white-space: pre-wrap;
      word-wrap: break-word;
      width: ${textarea.clientWidth}px;
      font: ${window.getComputedStyle(textarea).font};
      padding: ${window.getComputedStyle(textarea).padding};
      border: ${window.getComputedStyle(textarea).border};
      box-sizing: border-box;
    `;
    mirror.textContent = content + '\n'; // Extra newline for cursor line

    document.body.appendChild(mirror);
    const rows = Math.ceil(mirror.scrollHeight / this.lineHeight);
    document.body.removeChild(mirror);

    return Math.max(1, rows);
  }
}
